'use strict'

const DB_NAME = 'POSDatabase'
const DB_VERSION = 1
const STORE_NAME = 'products'

class POSIndexedDB {
  constructor() {
    this.db = null
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => {
        console.error('IndexedDB error:', request.error)
        reject(request.error)
      }

      request.onsuccess = () => {
        this.db = request.result
        resolve(this.db)
      }

      request.onupgradeneeded = (event) => {
        const db = event.target.result
        
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'store_id' })
          objectStore.createIndex('store_id', 'store_id', { unique: true })
          objectStore.createIndex('timestamp', 'timestamp', { unique: false })
        }
      }
    })
  }

  async saveProducts(storeId, products) {
    if (!this.db) {
      await this.init()
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite')
      const objectStore = transaction.objectStore(STORE_NAME)

      const data = {
        store_id: storeId,
        products: products,
        timestamp: new Date().toISOString()
      }

      const request = objectStore.put(data)

      request.onsuccess = () => {
        resolve(true)
      }

      request.onerror = () => {
        console.error('Error saving products to IndexedDB:', request.error)
        reject(request.error)
      }
    })
  }

  async getProducts(storeId) {
    if (!this.db) {
      await this.init()
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly')
      const objectStore = transaction.objectStore(STORE_NAME)
      const request = objectStore.get(storeId)

      request.onsuccess = () => {
        if (request.result) {
          resolve(request.result.products)
        } else {
          resolve(null)
        }
      }

      request.onerror = () => {
        console.error('Error getting products from IndexedDB:', request.error)
        reject(request.error)
      }
    })
  }

  async updateStockAndPrice(storeId, stockPriceUpdates) {
    if (!this.db) {
      await this.init()
    }

    return new Promise(async (resolve, reject) => {
      try {
        const cachedData = await this.getProducts(storeId)
        
        if (!cachedData) {
          resolve({ updated: false, hasNewProducts: false })
          return
        }

        const newProducts = stockPriceUpdates.filter(update => 
          !cachedData.some(product => product.product_id === update.product_id)
        )

        const updatedProducts = cachedData.map(product => {
          const update = stockPriceUpdates.find(u => u.product_id === product.product_id)
          if (update) {
            return {
              ...product,
              stock: update.stock,
              price: update.price
            }
          }
          return product
        })

        await this.saveProducts(storeId, updatedProducts)
        resolve({ 
          updated: updatedProducts, 
          hasNewProducts: newProducts.length > 0 
        })
      } catch (error) {
        console.error('Error updating stock and price:', error)
        reject(error)
      }
    })
  }

  async clearProducts(storeId) {
    if (!this.db) {
      await this.init()
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite')
      const objectStore = transaction.objectStore(STORE_NAME)
      const request = objectStore.delete(storeId)

      request.onsuccess = () => {
        resolve(true)
      }

      request.onerror = () => {
        console.error('Error clearing products from IndexedDB:', request.error)
        reject(request.error)
      }
    })
  }

  async clearAllProducts() {
    if (!this.db) {
      await this.init()
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite')
      const objectStore = transaction.objectStore(STORE_NAME)
      const request = objectStore.clear()

      request.onsuccess = () => {
        resolve(true)
      }

      request.onerror = () => {
        console.error('Error clearing all products from IndexedDB:', request.error)
        reject(request.error)
      }
    })
  }
}

export default new POSIndexedDB()
